import { SessionExpiredModal } from 'core/components/ui/SessionExpiredModal'
import { sessionEvents } from 'core/network/sessionEvents'
import { tokenStorage } from 'core/network/tokenStorage'
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { AuthRepositoryImpl } from '../../data/repositories/AuthRepositoryImpl'
import { User } from '../../domain/entities/User'
import { GetCurrentUserUseCase } from '../../domain/usecases/GetCurrentUserUseCase'
import { LoginUseCase } from '../../domain/usecases/LoginUseCase'
import { LogoutUseCase } from '../../domain/usecases/LogoutUseCase'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const authRepository = new AuthRepositoryImpl()
const loginUseCase = new LoginUseCase(authRepository)
const logoutUseCase = new LogoutUseCase(authRepository)
const getCurrentUserUseCase = new GetCurrentUserUseCase(authRepository)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = await getCurrentUserUseCase.execute()
        setUser(currentUser)
      } catch (err) {
        // NOSONAR: Logging initialization failure helps client session diagnostics
        // eslint-disable-next-line no-console
        console.error('Failed to get current user session', err) // NOSONAR
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  useEffect(() => {
    const unsubscribe = sessionEvents.subscribe(() => {
      setUser(null)
      setIsSessionExpired(true)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const handleSessionExpiredConfirm = () => {
    sessionEvents.reset()
    setIsSessionExpired(false)
    tokenStorage.clear()
    setUser(null)
    window.location.href = '/login'
  }

  const login = async (email: string, password: string): Promise<User> => {
    sessionEvents.reset()
    setIsSessionExpired(false)
    const loggedUser = await loginUseCase.execute(email, password)
    setUser(loggedUser)
    return loggedUser
  }

  const logout = async () => {
    setLoading(true)
    try {
      await logoutUseCase.execute()
      setUser(null)
    } catch (err) {
      // NOSONAR: Logging logout error for client session diagnostics
      // eslint-disable-next-line no-console
      console.error('Logout failed', err) // NOSONAR
    } finally {
      sessionEvents.reset()
      setIsSessionExpired(false)
      setLoading(false)
    }
  }

  const authValue = useMemo(() => ({ user, loading, login, logout }), [user, loading])

  return (
    <AuthContext.Provider value={authValue}>
      {children}
      <SessionExpiredModal isOpen={isSessionExpired} onConfirm={handleSessionExpiredConfirm} />
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
